import { styled } from "goober";
import { useCallback, useEffect, useState } from "react";
import type { ChatTool, ToolUseProps } from "../components/Chat/ChatTool";
import { ToolStatus } from "../components/Chat/ToolStatus";
import { ClickButton } from "../components/ClickButton";
import { Icon } from "../components/Icon";
import { Keycap } from "../components/Keycap";
import { PushLivePreflight } from "../components/PushLivePreflight";
import { Text } from "../components/Text";
import { mobileMedia } from "../hooks/useIsMobile";
import { useMainMergeStatus } from "../hooks/useMainMergeStatus";
import { usePushLive } from "../hooks/usePushLive";
import { maybeDisplayBrowserNotification } from "../lib/browserNotifications";
import type { PushLiveBranch } from "../lib/executePushLive";

type Input = { summary?: unknown };
type Output = { pushed?: unknown };

function parseSummary(input: unknown): string | null {
  if (!input || typeof input !== "object" || Array.isArray(input)) return null;
  const { summary } = input as Input;
  return typeof summary === "string" && summary.trim() ? summary : null;
}

function parseOutput(output: unknown): { pushed: boolean } | null {
  if (!output || typeof output !== "object" || Array.isArray(output))
    return null;
  const pushed = (output as Output).pushed;
  return typeof pushed === "boolean" ? { pushed } : null;
}

function PushLiveHeader({ result, interrupted }: ToolUseProps) {
  if (!result || result.isError) {
    return (
      <ToolStatus icon="rocket" text="Push live?" pending={!interrupted} />
    );
  }
  const output = parseOutput(result.output);
  if (!output?.pushed) {
    return <ToolStatus icon="circle-minus" text="Push live cancelled" />;
  }
  return (
    <ToolStatus icon="rocket" iconColor="var(--green-600)" text="Pushed live" />
  );
}

function PushLiveBody({ toolUse }: ToolUseProps) {
  const summary = parseSummary(toolUse.input);
  if (!summary) return null;
  return (
    <div style={{ padding: "2px 6px 8px 6px" }}>
      <Text variant="text-smaller-md" color="var(--text-tertiary)">
        {summary}
      </Text>
    </div>
  );
}

const PromptContainer = styled("div")`
  border-radius: 12px;
  outline: 0.5px solid var(--border-alpha);
  background: var(--surface-primary);
  box-shadow: var(--elevation-low);
  padding: 4px;
`;

const PromptBody = styled("div")`
  display: flex;
  flex-direction: column;
  padding-bottom: 4px;
  gap: 4px;
`;

const PromptHeader = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 8px;
  box-sizing: content-box;
  color: var(--text-secondary);
`;

const Row = styled(ClickButton)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  cursor: pointer;
  border-radius: 8px;
  text-align: left;
  width: 100%;
  box-sizing: border-box;
  &:hover:not(:disabled) {
    background: var(--alpha4);
  }
  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

const ActionIcon = styled("span")`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const Footer = styled("div")`
  display: flex;
  align-items: center;
  margin: 0 -4px -4px -4px;
  height: 40px;
  padding: 0 12px;
  border-top: 0.5px solid var(--border-alpha);
  justify-content: flex-end;
  ${mobileMedia} {
    display: none;
  }
`;

const Instructions = styled("div")`
  display: flex;
  align-items: center;
  color: var(--text-secondary);
  gap: 4px;
`;

type OnResult = (
  output: unknown,
  isError: boolean,
  options?: { pauseAfter?: boolean }
) => void;

function PushLivePrompt({
  summary,
  branch,
  mainBranchId,
  spaceId,
  canPushLive,
  canWrite,
  blockedBySelfMerge,
  gitSynced,
  onResult,
}: {
  summary: string | null;
  branch: PushLiveBranch | undefined;
  mainBranchId: string | undefined;
  spaceId: string;
  canPushLive: boolean;
  canWrite: boolean;
  blockedBySelfMerge: boolean;
  gitSynced: boolean;
  onResult: OnResult;
}) {
  const { pushLive, mergeMain } = usePushLive({
    branchId: branch?.id ?? "",
    spaceId,
  });
  const mergeStatus = useMainMergeStatus(branch?.id ?? "", mainBranchId);
  const [preflightOpen, setPreflightOpen] = useState(false);

  useEffect(() => {
    maybeDisplayBrowserNotification("3B needs you", "Push live?");
  }, []);

  const cancel = useCallback(
    () => onResult({ pushed: false }, false, { pauseAfter: true }),
    [onResult]
  );

  const confirm = useCallback(() => {
    onResult({ pushed: true }, false, { pauseAfter: true });
    void pushLive(branch, mainBranchId);
  }, [onResult, pushLive, branch, mainBranchId]);

  const resolveConflicts = useCallback(() => {
    onResult({ pushed: false }, false, { pauseAfter: true });
    mergeMain();
  }, [onResult, mergeMain]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (preflightOpen) return;
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        cancel();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [cancel, preflightOpen]);

  const blockedReason = gitSynced
    ? "This space syncs with a git repository. Sync the draft and merge it on the remote instead."
    : !canPushLive
      ? "You don’t have permission to push live in this space."
      : blockedBySelfMerge
        ? "This space requires a different user to push branches live."
        : !branch || branch.name === "main"
          ? "There’s nothing to push — you’re on the live branch."
          : null;

  return (
    <>
      <PromptContainer>
        <PromptBody>
          <PromptHeader>
            <Text variant="text-small">Push this workflow live?</Text>
            <Text variant="text-smaller-md" color="var(--text-tertiary)">
              {summary ?? "Promotes your draft to the live main branch."}
            </Text>
          </PromptHeader>
          {blockedReason ? (
            <>
              <PromptHeader>
                <Text variant="text-smaller-md" color="var(--text-tertiary)">
                  {blockedReason}
                </Text>
              </PromptHeader>
              <Row data-no-pill onClick={cancel}>
                <ActionIcon>
                  <Icon
                    name="circle-minus"
                    size={16}
                    color="var(--text-primary)"
                  />
                </ActionIcon>
                <Text variant="text-regular" color="var(--text-primary)">
                  Dismiss
                </Text>
              </Row>
            </>
          ) : mergeStatus === "unresolved" ? (
            <>
              <PromptHeader>
                <Text variant="text-smaller-md" color="var(--text-tertiary)">
                  This draft has unresolved conflicts. Resolve them before
                  pushing live.
                </Text>
              </PromptHeader>
              <Row data-no-pill onClick={cancel}>
                <ActionIcon>
                  <Icon
                    name="circle-minus"
                    size={16}
                    color="var(--text-primary)"
                  />
                </ActionIcon>
                <Text variant="text-regular" color="var(--text-primary)">
                  Dismiss
                </Text>
              </Row>
            </>
          ) : mergeStatus === "conflicts" ? (
            <>
              <PromptHeader>
                <Text variant="text-smaller-md" color="var(--text-tertiary)">
                  {canWrite
                    ? "Live has changes that conflict with this draft. Pull them in and resolve the conflicts before pushing live."
                    : "Live has changes that conflict with this draft. Someone with edit access needs to pull them in and resolve the conflicts before it can be pushed live."}
                </Text>
              </PromptHeader>
              {canWrite && (
                <Row data-no-pill onClick={resolveConflicts}>
                  <ActionIcon>
                    <Icon name="merge" size={16} color="var(--text-primary)" />
                  </ActionIcon>
                  <Text variant="text-regular" color="var(--text-primary)">
                    Pull in live changes
                  </Text>
                </Row>
              )}
              <Row data-no-pill onClick={cancel}>
                <ActionIcon>
                  <Icon
                    name="circle-minus"
                    size={16}
                    color="var(--text-primary)"
                  />
                </ActionIcon>
                <Text variant="text-regular" color="var(--text-primary)">
                  {canWrite ? "Cancel" : "Dismiss"}
                </Text>
              </Row>
            </>
          ) : (
            <>
              <Row
                data-no-pill
                disabled={mergeStatus === "loading"}
                onClick={() => setPreflightOpen(true)}
              >
                <ActionIcon>
                  <Icon name="rocket" size={16} color="var(--text-primary)" />
                </ActionIcon>
                <Text variant="text-regular" color="var(--text-primary)">
                  Push live
                </Text>
              </Row>
              <Row data-no-pill onClick={cancel}>
                <ActionIcon>
                  <Icon
                    name="circle-minus"
                    size={16}
                    color="var(--text-primary)"
                  />
                </ActionIcon>
                <Text variant="text-regular" color="var(--text-primary)">
                  Cancel
                </Text>
              </Row>
            </>
          )}
        </PromptBody>
        <Footer>
          <Instructions>
            <Text variant="text-smaller-md">Cancel</Text>
            <Keycap style={{ padding: "2px 4px" }}>
              <Text variant="text-smaller-md">esc</Text>
            </Keycap>
          </Instructions>
        </Footer>
      </PromptContainer>
      {preflightOpen && branch && (
        <PushLivePreflight
          workflowId={branch.workflowId}
          branchId={branch.id}
          spaceId={spaceId}
          onProceed={() => {
            setPreflightOpen(false);
            confirm();
          }}
          onCancel={() => setPreflightOpen(false)}
        />
      )}
    </>
  );
}

export const pushLive = (opts: {
  branch: PushLiveBranch | undefined;
  mainBranchId: string | undefined;
  spaceId: string;
  canPushLive: boolean;
  canWrite: boolean;
  blockedBySelfMerge: boolean;
  gitSynced: boolean;
}): ChatTool => ({
  name: "pushLive",
  Header: PushLiveHeader,
  Body: PushLiveBody,
  alwaysLoadOutput: true,
  renderPendingPrompt: ({ input, onResult }) => (
    <PushLivePrompt
      summary={parseSummary(input)}
      branch={opts.branch}
      mainBranchId={opts.mainBranchId}
      spaceId={opts.spaceId}
      canPushLive={opts.canPushLive}
      canWrite={opts.canWrite}
      blockedBySelfMerge={opts.blockedBySelfMerge}
      gitSynced={opts.gitSynced}
      onResult={onResult}
    />
  ),
});
